import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { authService } from '../../services';
import routes from '../../utils/routes';
import './ResetPassword.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Try to get parameters from location state first
  let resetCode = location.state?.resetCode;
  let email = location.state?.email;
  
  // If parameters are missing, try to get them from session storage
  if (!resetCode || !email) {
    try {
      const storedState = sessionStorage.getItem('resetPasswordState');
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        if (!resetCode) resetCode = parsedState.resetCode;
        if (!email) email = parsedState.email;
        console.log('Retrieved reset password state from session storage:', parsedState);
      }
    } catch (error) {
      console.error('Error reading from session storage:', error);
    }
  }

  // Detailed debugging information
  console.log('Reset Password Page - Component mounted');
  console.log('Reset Password Page - Location state:', location.state);
  console.log('Reset Password Page - Parameters after recovery:', { 
    resetCode, 
    email,
    hasResetCode: !!resetCode,
    hasEmail: !!email,
    location: window.location.href
  });

  // Redirect if no code or email is provided
  useEffect(() => {
    if (!resetCode || !email) {
      console.warn('Missing required reset parameters, redirecting to forgot password');
      console.warn('Current state:', { resetCode, email, locationState: location.state });
      
      // Show an alert in development to help with debugging
      if (process.env.NODE_ENV !== 'production') {
        alert(`Missing required password reset parameters. 
Reset code: ${resetCode ? 'Yes' : 'No'}
Email: ${email ? 'Yes' : 'No'}`);
      }
      
      navigate(routes.public.FORGOT_PASSWORD);
    }
  }, [resetCode, email, navigate, location.state]);

  // Form validation schema
  const validationSchema = Yup.object({
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[^a-zA-Z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required'),
  });

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setServerError('');
        
        console.log('Submitting reset password with all three required fields:', {
          email,
          resetCode,
          passwordLength: values.password.length
        });
        
        try {
          // Call reset password API with all three required fields:
          // 1. Email (from state/session storage)
          // 2. Reset code/OTP (from state/session storage)
          // 3. New password (from form)
          const response = await authService.resetPassword(email, resetCode, values.password);
          console.log('Reset password success:', response.data);
          
          // Show success message
          setIsSuccess(true);
          
          // Clean up session storage
          try {
            sessionStorage.removeItem('resetPasswordState');
            console.log('Reset password state cleared from session storage');
          } catch (error) {
            console.error('Error clearing session storage:', error);
          }
          
          // Navigate to login page after a delay
          setTimeout(() => {
            navigate(routes.public.LOGIN, {
              state: { message: 'Password reset successful. You can now log in with your new password.' }
            });
          }, 3000);
        } catch (apiError) {
          console.error('API Error during password reset:', apiError);
          
          if (apiError.response) {
            console.error('Response data:', apiError.response.data);
            console.error('Response status:', apiError.response.status);
            
            const errorMessage = apiError.response.data?.message || 
                              apiError.response.data?.error ||
                              'Failed to reset password. Please try again.';
            setServerError(errorMessage);
          } else {
            setServerError('An error occurred during password reset. Please try again.');
          }
        }
      } catch (error) {
        console.error('Unexpected reset password error:', error);
        setServerError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <div className="reset-password-title">Reset Password</div>
          <div className="reset-password-subtitle">
            Create a new password for your account
          </div>
        </div>

        {isSuccess ? (
          <div className="reset-password-success">
            <p>Your password has been reset successfully!</p>
            <p>Redirecting to login page...</p>
          </div>
        ) : (
          <>
            {serverError && (
              <div className="error-message" style={{ marginBottom: 'var(--spacing-md)' }}>
                {serverError}
              </div>
            )}

            <form className="reset-password-form" onSubmit={formik.handleSubmit}>
              <Input
                name="password"
                type="password"
                label="New Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && formik.errors.password}
                placeholder="Enter new password"
                required
              />

              <Input
                name="confirmPassword"
                type="password"
                label="Confirm New Password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && formik.errors.confirmPassword}
                placeholder="Confirm new password"
                required
              />

              <Button
                type="submit"
                variant="primary"
                className="reset-password-submit-button"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}

        <div className="reset-password-back">
          <Link to={routes.public.LOGIN}>Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 